import { getSubMassParentTag } from "building-types/buildingGeomTag/buildingGeomTags";
import type { GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import type { WallPlaneRoofSelection } from "building-types/walls2/wallPlane";
import {
  WallPlaneRoofClippingOperationMode,
  WallPlaneRoofClippingSplitDirection,
} from "building-types/walls2/wallPlane";
import { WallType } from "building-types/walls/wallEnums";
import type { WallFaces } from "building-types/walls/wallFacesTypes";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { SolidWall, Wall } from "building-types/walls/wallTypes";
import type { RWTable, Table } from "common/data/table";
import uid from "common/utils/uid";
import type { Face, FaceTagBase } from "geom/brep";
import { splitFacesBSP } from "geom/bspClipping";
import type { NodeBSP } from "geom/bspTypes";

export function clipWallsWithRoofClippingOperations(
  wallSystem: WallSystem,
  roofs: Table<GeneratedRoofSystem>,
): WallSystem {
  const newWalls: RWTable<Wall> = { ...wallSystem.walls };

  for (const wallId in wallSystem.walls) {
    const wall = wallSystem.walls[wallId];

    if (wall.wallType !== WallType.SOLID) {
      continue;
    }

    const { roofClippingOpsBySubMassId } = wall.mass;

    if (!roofClippingOpsBySubMassId) {
      continue;
    }

    let newWallFaces: WallFaces = wall.wallFaces;
    let newWallOutlineFaces: WallFaces = wall.wallOutlineFaces;

    for (const [subMassId, operations] of roofClippingOpsBySubMassId) {
      for (const operation of operations) {
        if (operation.operationBoundary?.boundaryType !== "ROOF_CLIPPING") {
          throw new Error(
            "Unsupported operation boundary type in roof clipping operation: " +
              operation.operationBoundary?.boundaryType,
          );
        }

        const roofBSPs = getSelectedRoofBSPs(operation.operationBoundary.roofSelection, roofs);

        if (roofBSPs.length === 0) {
          continue;
        }

        const splitWallFaces = splitWallFacesWithRoofBSPs(
          newWallFaces,
          roofBSPs,
          operation.operationBoundary.splitDirection,
          subMassId,
        );
        const splitWallOutlineFaces = splitWallFacesWithRoofBSPs(
          newWallOutlineFaces,
          roofBSPs,
          operation.operationBoundary.splitDirection,
          subMassId,
        );

        if (operation.wallPlaneOperator.operationType === "SET_WALL_CUT") {
          if (
            operation.operationBoundary.splitDirection === WallPlaneRoofClippingSplitDirection.BELOW_ANY ||
            operation.operationBoundary.splitDirection === WallPlaneRoofClippingSplitDirection.BELOW_ALL
          ) {
            newWallFaces = splitWallFaces.aboveFaces;
            newWallOutlineFaces = splitWallOutlineFaces.aboveFaces;
          } else {
            newWallFaces = splitWallFaces.belowFaces;
            newWallOutlineFaces = splitWallOutlineFaces.belowFaces;
          }
        } else {
          throw new Error("Unsupported roof clipping operation type: " + operation.wallPlaneOperator.operationType);
        }
      }
    }

    const newWall: SolidWall = { ...wall, wallFaces: newWallFaces, wallOutlineFaces: newWallOutlineFaces };
    newWalls[wallId] = newWall;
  }

  return { ...wallSystem, id: uid(), walls: newWalls };
}

function getSelectedRoofBSPs(roofSelection: WallPlaneRoofSelection, roofs: Table<GeneratedRoofSystem>): NodeBSP[] {
  if (roofSelection.mode === WallPlaneRoofClippingOperationMode.NONE) {
    return [];
  }

  const isAll = roofSelection.mode === WallPlaneRoofClippingOperationMode.ALL;
  const selectedRoofIds =
    roofSelection.mode === WallPlaneRoofClippingOperationMode.SELECTED
      ? new Set<string>(roofSelection.roofIds)
      : undefined;

  const bsps: NodeBSP[] = [];
  for (const roofSystemId in roofs) {
    const individualBSPs = roofs[roofSystemId].clipContext.individualInsideBSPs;
    if (!individualBSPs) {
      continue;
    }

    for (const subRoofId in individualBSPs) {
      const bsp = individualBSPs[subRoofId];
      if (bsp && (isAll || selectedRoofIds?.has(subRoofId))) {
        bsps.push(bsp);
      }
    }
  }

  return bsps;
}

function splitWallFacesWithRoofBSPs(
  wallFaces: WallFaces,
  roofBSPs: NodeBSP[],
  direction: WallPlaneRoofClippingSplitDirection,
  subMassId: string,
): { aboveFaces: WallFaces; belowFaces: WallFaces } {
  const splitFaces = (faces: Face[]) => splitSubMassFacesWithBSPs(faces, roofBSPs, direction, subMassId);

  const leftSplitFaces = splitFaces(wallFaces.leftFaces);
  const rightSplitFaces = splitFaces(wallFaces.rightFaces);
  const topSplitFaces = splitFaces(wallFaces.topFaces);
  const bottomSplitFaces = splitFaces(wallFaces.bottomFaces);
  const startSplitFaces = splitFaces(wallFaces.startFaces);
  const endSplitFaces = splitFaces(wallFaces.endFaces);
  const leftHoleSplitFaces = splitFaces(wallFaces.leftHoleFaces);
  const rightHoleSplitFaces = splitFaces(wallFaces.rightHoleFaces);
  const leftExteriorSplitFaces = splitFaces(wallFaces.leftExteriorFaces);
  const rightExteriorSplitFaces = splitFaces(wallFaces.rightExteriorFaces);

  const aboveFaces: WallFaces = {
    leftFaces: leftSplitFaces.aboveFaces,
    rightFaces: rightSplitFaces.aboveFaces,
    topFaces: topSplitFaces.aboveFaces,
    bottomFaces: bottomSplitFaces.aboveFaces,
    startFaces: startSplitFaces.aboveFaces,
    endFaces: endSplitFaces.aboveFaces,
    leftHoleFaces: leftHoleSplitFaces.aboveFaces,
    rightHoleFaces: rightHoleSplitFaces.aboveFaces,
    leftExteriorFaces: leftExteriorSplitFaces.aboveFaces,
    rightExteriorFaces: rightExteriorSplitFaces.aboveFaces,
  };
  const belowFaces: WallFaces = {
    leftFaces: leftSplitFaces.belowFaces,
    rightFaces: rightSplitFaces.belowFaces,
    topFaces: topSplitFaces.belowFaces,
    bottomFaces: bottomSplitFaces.belowFaces,
    startFaces: startSplitFaces.belowFaces,
    endFaces: endSplitFaces.belowFaces,
    leftHoleFaces: leftHoleSplitFaces.belowFaces,
    rightHoleFaces: rightHoleSplitFaces.belowFaces,
    leftExteriorFaces: leftExteriorSplitFaces.belowFaces,
    rightExteriorFaces: rightExteriorSplitFaces.belowFaces,
  };

  return { aboveFaces, belowFaces };
}
export interface SplitFaceWithBSPsResult {
  aboveFaces: Face[];
  belowFaces: Face[];
}

function splitDirectionUsesNarrowAbove(direction: WallPlaneRoofClippingSplitDirection): boolean {
  return (
    direction === WallPlaneRoofClippingSplitDirection.BELOW_ANY ||
    direction === WallPlaneRoofClippingSplitDirection.ABOVE_ALL
  );
}

export function splitSubMassFacesWithBSPs(
  faces: Face[],
  insideBSPs: NodeBSP[],
  direction: WallPlaneRoofClippingSplitDirection,
  subMassId: string,
): SplitFaceWithBSPsResult {
  const splitPredicate = splitDirectionUsesNarrowAbove(direction)
    ? splitFaceWithBSPsNarrowAbove
    : splitFaceWithBSPsNarrowBelow;

  const aboveFaces: Face[] = [];
  const belowFaces: Face[] = [];

  for (const face of faces) {
    if (getSubMassParentTag(face)?.parentId !== subMassId) {
      // if the face is not part of the submass, add it to the group that is *not* selected by the split direction
      if (
        direction === WallPlaneRoofClippingSplitDirection.BELOW_ALL ||
        direction === WallPlaneRoofClippingSplitDirection.BELOW_ANY
      ) {
        aboveFaces.push(face);
      } else {
        belowFaces.push(face);
      }
      continue;
    }
    const result = splitPredicate(face, insideBSPs);
    copyFaceTags(face, result.aboveFaces);
    copyFaceTags(face, result.belowFaces);
    aboveFaces.push(...result.aboveFaces);
    belowFaces.push(...result.belowFaces);
  }

  return { aboveFaces: aboveFaces, belowFaces: belowFaces };
}

function splitFaceWithBSPsNarrowAbove(face: Face, insideBSPs: NodeBSP[]): SplitFaceWithBSPsResult {
  let aboveCandidates: Face[] = [face];
  const belowFaces: Face[] = [];

  // Narrow above candidates with each BSP; below fragments are accumulated and not re-split
  for (const insideBSP of insideBSPs) {
    const newAbove: Face[] = [];
    const newBelow: Face[] = [];

    splitFacesBSP(aboveCandidates, {
      node: insideBSP,
      coplanarsToFront: false,
      frontFaces: newAbove,
      backFaces: newBelow,
    });

    belowFaces.push(...newBelow);
    aboveCandidates = newAbove;
  }

  return { aboveFaces: aboveCandidates, belowFaces };
}

function splitFaceWithBSPsNarrowBelow(face: Face, insideBSPs: NodeBSP[]): SplitFaceWithBSPsResult {
  let belowCandidates: Face[] = [face];
  const aboveFaces: Face[] = [];

  // Narrow below candidates with each BSP; above fragments are accumulated and not re-split
  for (const insideBSP of insideBSPs) {
    const newAbove: Face[] = [];
    const newBelow: Face[] = [];

    splitFacesBSP(belowCandidates, {
      node: insideBSP,
      coplanarsToFront: false,
      frontFaces: newAbove,
      backFaces: newBelow,
    });
    aboveFaces.push(...newAbove);
    belowCandidates = newBelow;
  }

  return { aboveFaces, belowFaces: belowCandidates };
}

function copyFaceTags(source: Face, faces: Face[]): void {
  if (!source.tags) {
    return;
  }

  const tags: FaceTagBase[] = source.tags;
  for (const f of faces) {
    f.tags = [...tags];
  }
}
